/**
 * Calendar Page - Google Calendar Integration
 * View synced events, create appointments, manage calendar connection
 */
import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Calendar as CalendarIcon, Plus, RefreshCw, Settings as SettingsIcon,
  Clock, MapPin, Users, ExternalLink
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import api from '../lib/api';

export default function CalendarPage() {
  const [connected, setConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // New event form
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    location: '',
    start_time: '',
    end_time: '',
    attendees: ''
  });

  useEffect(() => {
    checkConnectionStatus();
  }, []);

  const checkConnectionStatus = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/integrations/google/calendar/status`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      const data = await response.json();
      
      if (data.connected) {
        setConnected(true);
        setConnectionStatus(data);
        loadEvents();
      } else {
        setConnected(false);
        setLoading(false);
      }
    } catch (error) {
      console.error('Failed to check calendar status:', error);
      setLoading(false);
    }
  };

  const connectCalendar = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/integrations/google/calendar/connect`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      const data = await response.json();
      
      // Open OAuth flow in popup
      const width = 500;
      const height = 600;
      const left = (window.innerWidth - width) / 2;
      const top = (window.innerHeight - height) / 2;
      
      const popup = window.open(
        data.authorization_url,
        'Google Calendar Authorization',
        `width=${width},height=${height},left=${left},top=${top}`
      );
      
      // Poll for popup close (simplified - production would use postMessage)
      const pollTimer = setInterval(() => {
        if (popup.closed) {
          clearInterval(pollTimer);
          toast.success('Checking connection status...');
          setTimeout(() => checkConnectionStatus(), 2000);
        }
      }, 500);
      
    } catch (error) {
      console.error('Failed to connect calendar:', error);
      toast.error('Failed to connect calendar');
    }
  };

  const loadEvents = async () => {
    try {
      setSyncing(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/calendar/events`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      const data = await response.json();
      setEvents(data.events || []);
    } catch (error) {
      console.error('Failed to load events:', error);
    } finally {
      setSyncing(false);
      setLoading(false);
    }
  };

  const createEvent = async () => {
    try {
      // Convert attendees string to array
      const attendeesArray = newEvent.attendees
        .split(',')
        .map(e => e.trim())
        .filter(e => e);

      const eventData = {
        title: newEvent.title,
        description: newEvent.description,
        location: newEvent.location,
        start_time: new Date(newEvent.start_time).toISOString(),
        end_time: new Date(newEvent.end_time).toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        attendees: attendeesArray
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/calendar/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(eventData)
      });

      if (response.ok) {
        toast.success('Meeting created successfully!', {
          description: 'Calendar invite sent to attendees'
        });
        setCreateDialogOpen(false);
        setNewEvent({ title: '', description: '', location: '', start_time: '', end_time: '', attendees: '' });
        loadEvents();
      } else {
        const error = await response.json();
        toast.error('Failed to create meeting', { description: error.detail });
      }
    } catch (error) {
      console.error('Failed to create event:', error);
      toast.error('Failed to create meeting');
    }
  };

  const disconnectCalendar = async () => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/integrations/google/calendar/disconnect`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      setConnected(false);
      setEvents([]);
      toast.success('Calendar disconnected');
    } catch (error) {
      toast.error('Failed to disconnect calendar');
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Loading calendar...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!connected) {
    return (
      <div className="p-8">
        <div className="max-w-2xl mx-auto">
          <Card className="p-12 text-center">
            <div className="w-20 h-20 bg-[#3b82f6]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <CalendarIcon className="h-10 w-10 text-[#3b82f6]" />
            </div>
            
            <h2 className="text-2xl font-bold mb-4">Connect Your Google Calendar</h2>
            
            <p className="text-muted-foreground mb-8 text-lg">
              Sync your appointments, schedule meetings with leads, and manage your calendar directly from ISOCNTRL.
            </p>
            
            <div className="space-y-4 text-left bg-accent/50 p-6 rounded-lg mb-8">
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-xs font-bold">✓</span>
                </div>
                <div>
                  <p className="font-medium">Two-way sync</p>
                  <p className="text-sm text-muted-foreground">Events created in CRM appear in Google Calendar and vice versa</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-xs font-bold">✓</span>
                </div>
                <div>
                  <p className="font-medium">Automatic invites</p>
                  <p className="text-sm text-muted-foreground">Meeting invites sent to leads automatically</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-xs font-bold">✓</span>
                </div>
                <div>
                  <p className="font-medium">Lead linkage</p>
                  <p className="text-sm text-muted-foreground">See all meetings directly in lead detail pages</p>
                </div>
              </div>
            </div>
            
            <Button 
              onClick={connectCalendar} 
              size="lg"
              className="bg-[#3b82f6] hover:bg-[#2563eb] text-white px-8"
            >
              <CalendarIcon className="mr-2 h-5 w-5" />
              Connect Google Calendar
            </Button>
            
            <p className="text-xs text-muted-foreground mt-6">
              Your calendar data is private and secure. We only access what you authorize.
            </p>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2">Calendar</h1>
          <p className="text-muted-foreground">
            Connected to {connectionStatus?.email}
          </p>
        </div>
        
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            size="sm"
            onClick={loadEvents}
            disabled={syncing}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync'}
          </Button>
          
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#3b82f6] hover:bg-[#2563eb]">
                <Plus className="mr-2 h-4 w-4" />
                New Meeting
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle>Schedule New Meeting</DialogTitle>
                <DialogDescription>
                  Create a meeting and send calendar invites to attendees
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="title">Meeting Title *</Label>
                  <Input
                    id="title"
                    value={newEvent.title}
                    onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                    placeholder="e.g., Demo Call with Prospect"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={newEvent.description}
                    onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                    placeholder="Meeting agenda or notes..."
                    className="mt-1"
                    rows={3}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="start">Start Time *</Label>
                    <Input
                      id="start"
                      type="datetime-local"
                      value={newEvent.start_time}
                      onChange={(e) => setNewEvent({ ...newEvent, start_time: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="end">End Time *</Label>
                    <Input
                      id="end"
                      type="datetime-local"
                      value={newEvent.end_time}
                      onChange={(e) => setNewEvent({ ...newEvent, end_time: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    value={newEvent.location}
                    onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                    placeholder="Zoom link, address, or phone number"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="attendees">Attendees (comma-separated emails)</Label>
                  <Input
                    id="attendees"
                    value={newEvent.attendees}
                    onChange={(e) => setNewEvent({ ...newEvent, attendees: e.target.value })}
                    placeholder="john@example.com, jane@example.com"
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={createEvent}
                  disabled={!newEvent.title || !newEvent.start_time || !newEvent.end_time}
                  className="bg-[#3b82f6] hover:bg-[#2563eb]"
                >
                  Create Meeting
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          
          <Button variant="outline" size="sm" onClick={disconnectCalendar}>
            <SettingsIcon className="mr-2 h-4 w-4" />
            Settings
          </Button>
        </div>
      </div>

      {/* Events List */}
      {events.length === 0 ? (
        <Card className="p-12 text-center">
          <CalendarIcon className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
          <h3 className="text-xl font-semibold mb-2">No upcoming events</h3>
          <p className="text-muted-foreground mb-6">
            Create your first meeting or wait for your calendar to sync
          </p>
          <Button onClick={loadEvents} variant="outline">
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh Calendar
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {events.map((event, idx) => {
            const startTime = new Date(event.start?.dateTime || event.start?.date);
            const endTime = new Date(event.end?.dateTime || event.end?.date);
            const isToday = startTime.toDateString() === new Date().toDateString();
            const isPast = startTime < new Date();
            
            return (
              <Card key={idx} className={`p-6 ${isPast ? 'opacity-60' : ''}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <h3 className="text-lg font-semibold">{event.summary || 'Untitled Event'}</h3>
                      {isToday && <Badge className="bg-green-500">Today</Badge>}
                      {isPast && <Badge variant="outline">Past</Badge>}
                    </div>
                    
                    {event.description && (
                      <p className="text-sm text-muted-foreground mb-3">{event.description}</p>
                    )}
                    
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center space-x-2">
                        <Clock className="h-4 w-4" />
                        <span>
                          {startTime.toLocaleDateString()} {startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          {' - '}
                          {endTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      
                      {event.location && (
                        <div className="flex items-center space-x-2">
                          <MapPin className="h-4 w-4" />
                          <span>{event.location}</span>
                        </div>
                      )}
                      
                      {event.attendees && event.attendees.length > 0 && (
                        <div className="flex items-center space-x-2">
                          <Users className="h-4 w-4" />
                          <span>{event.attendees.length} attendee{event.attendees.length > 1 ? 's' : ''}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {event.htmlLink && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => window.open(event.htmlLink, '_blank')}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
      
      {/* Connection Info Footer */}
      <Card className="mt-8 p-4 bg-accent/30">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-2 text-muted-foreground">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span>Connected to Google Calendar</span>
          </div>
          {connectionStatus?.last_sync && (
            <span className="text-muted-foreground">
              Last synced: {new Date(connectionStatus.last_sync).toLocaleString()}
            </span>
          )}
        </div>
      </Card>
    </div>
  );
}
